// Fiscal territory helpers (France Métropolitaine + DROM).
//
// We store the chosen territory in profiles.rates_json.tax_territory.
// This module centralizes:
// - VAT defaults and common rates by territory
// - Optional URSSAF DOM reduced rates presets (progressive on 3 periods)

export const TAX_TERRITORIES = {
  metropole: {
    key: 'metropole',
    label: 'France métropolitaine',
    isDom: false,
    vat: {
      applicable: true,
      defaultRatePercent: 20,
      ratesPercent: [20, 10, 5.5, 2.1, 0],
    },
  },

  guadeloupe: {
    key: 'guadeloupe',
    label: 'Guadeloupe',
    isDom: true,
    vat: {
      applicable: true,
      defaultRatePercent: 8.5,
      ratesPercent: [8.5, 2.1, 1.75, 1.05, 0],
    },
  },

  martinique: {
    key: 'martinique',
    label: 'Martinique',
    isDom: true,
    vat: {
      applicable: true,
      defaultRatePercent: 8.5,
      ratesPercent: [8.5, 2.1, 1.75, 1.05, 0],
    },
  },

  reunion: {
    key: 'reunion',
    label: 'La Réunion',
    isDom: true,
    vat: {
      applicable: true,
      defaultRatePercent: 8.5,
      ratesPercent: [8.5, 2.1, 1.75, 1.05, 0],
    },
  },

  // Guyane : hors champ TVA (TVA non applicable) — mais reste un DROM.
  guyane: {
    key: 'guyane',
    label: 'Guyane',
    isDom: true,
    vat: {
      applicable: false,
      defaultRatePercent: 0,
      ratesPercent: [0],
    },
  },
};

export function normalizeTerritory(input) {
  const v = String(input || '').trim().toLowerCase();
  if (!v) return 'metropole';

  // Accept common synonyms
  if (v === 'fr' || v === 'france' || v === 'metropolitan' || v === 'metropolitan_france') return 'metropole';
  if (v === 'guadeloupe') return 'guadeloupe';
  if (v === 'martinique') return 'martinique';
  if (v === 'reunion' || v === 'la_reunion' || v === 'réunion' || v === 'la réunion') return 'reunion';
  if (v === 'guyane' || v === 'french_guiana' || v === 'guyane_francaise') return 'guyane';

  return TAX_TERRITORIES[v] ? v : 'metropole';
}

export function getTerritoryConfig(input) {
  const key = normalizeTerritory(input);
  return TAX_TERRITORIES[key] || TAX_TERRITORIES.metropole;
}

export function isDomTerritory(input) {
  return getTerritoryConfig(input)?.isDom === true;
}

export function isVatApplicableInTerritory(input) {
  return getTerritoryConfig(input)?.vat?.applicable === true;
}

export function getDefaultVatRatePercent(input) {
  return Number(getTerritoryConfig(input)?.vat?.defaultRatePercent ?? 20);
}

export function getVatRateOptions(input) {
  const cfg = getTerritoryConfig(input);
  const rates = Array.isArray(cfg?.vat?.ratesPercent) ? cfg.vat.ratesPercent : [20, 10, 5.5, 2.1, 0];
  return rates.map((r) => {
    const isInt = Number.isFinite(r) && Math.abs(r - Math.round(r)) < 1e-9;
    const label = isInt ? `${Math.round(r)}%` : `${String(r).replace('.', ',')}%`;
    return { value: String(r), label, rate: Number(r) };
  });
}

// --- Optional URSSAF DOM reduced rates presets (micro-entreprise)
// The app already lets the user override rates manually.
// These presets are meant as helpers / defaults.

const DOM_URSSAF_PRESETS = {
  // Periods (1 -> year 1, 2 -> year 2, 3 -> year 3)
  // Values are "global" micro-social rates (%)
  // Note: this is a simplified mapping to the app's activity_type.
  sales: { 1: 2.1, 2: 6.2, 3: 8.2 },
  services: { 1: 3.6, 2: 10.6, 3: 14.2 },
  mixed: { 1: 3.6, 2: 10.6, 3: 14.2 },
};

export function getDomUrssafBaseRatePercent({ activityType = 'services', period = 1 } = {}) {
  const at = (String(activityType || 'services').toLowerCase() === 'sales') ? 'sales'
    : (String(activityType || 'services').toLowerCase() === 'mixed') ? 'mixed'
      : 'services';
  const p = Number(period) || 1;
  const rate = DOM_URSSAF_PRESETS?.[at]?.[p];
  return Number.isFinite(Number(rate)) ? Number(rate) : null;
}
